import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Map, ChevronRight, ChevronLeft, Clock, Check, X, Plus, RefreshCw, MapPin, ArrowRight, Navigation, Utensils, Soup, CloudRain, MoonStar, MessageCircleMore } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { visitTiming, visitTimingStatus, upcomingVisit } from "../utils/companionTiming";
import TripPreferenceRecovery from "../components/TripPreferenceRecovery";
import { API, placeImg } from "../utils/constants";
import { formatDuration } from "../utils/smartDuration";
import { groupedTips, contextualTip, tipText, beforeYouGo, cashStops, LABELS } from "../utils/tips";
import { FanMark, Wordmark } from "../components/FanMark";

// ── NOW helpers — make the companion time-aware, using data the itinerary
// already carries (transit_to_next, best_hours, what_to_order, insider_note).
const parseDateLocal = (s) => {
  if (!s || typeof s !== "string") return null;
  const [y, m, d] = s.split("-").map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
};
const japanClock = () => Object.fromEntries(new Intl.DateTimeFormat("en-CA", { timeZone: "Asia/Tokyo", year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit", hourCycle: "h23" }).formatToParts(new Date()).filter(p => p.type !== "literal").map(p => [p.type, p.value]));
const todayLocal = () => { const n = japanClock(); return new Date(+n.year, +n.month - 1, +n.day); };
const dayDiff = (a, b) => Math.round((a - b) / 86400000);

const orderTip = (loc) => (loc?.tips || []).find(t => /^order:/i.test(t));
const plainTips = (loc) => (loc?.tips || []).filter(t => !/^order:/i.test(t));

const mapsDirUrl = (loc, city) => {
  const dest = encodeURIComponent(`${loc.name}, ${loc.city || city || "Japan"}, Japan`);
  return `https://www.google.com/maps/dir/?api=1&destination=${dest}`;
};

// First stop today that isn't visited, skipped, or advanced-past, plus the
// transit leg into it. `skip` self-heals a forgotten "Done" tap.
const visitKey = loc => loc.visit_id || loc.name;
const rescueForVisit = (pack, loc) => (loc?.visit_id && pack?.by_visit?.[loc.visit_id]) || pack?.by_stop?.[loc?.name] || {};
// Food alternatives stay visible in the day, but aren't scheduled obligations.
// A planned dinner remains a stop: scheduling comes from is_optional, not category.
const scheduledLocations = block => (block?.locations || []).filter(loc => !loc.is_optional);
const scheduledStops = day => (day?.time_blocks || []).flatMap(scheduledLocations);
const buildWizardChecklist = (days, checklist) => Object.fromEntries(
  days.flatMap(scheduledStops).map(loc => [visitKey(loc), checklist[visitKey(loc)] === "completed" ? "completed" : "pending"])
);

const findNextStop = (day, checklist, skip) => {
  if (!day?.time_blocks) return null;
  for (let bi = 0; bi < day.time_blocks.length; bi++) {
    const block = day.time_blocks[bi];
    for (const loc of scheduledLocations(block)) {
      const st = checklist[visitKey(loc)];
      if (st === "completed" || st === "skipped") continue;
      if (skip && skip.has(visitKey(loc))) continue;
      const prev = bi > 0 ? day.time_blocks[bi - 1] : null;
      return { loc, block, incomingTransit: prev?.transit_to_next || null };
    }
  }
  return null; // everything today is done
};

const dayStats = (day, checklist) => {
  let total = 0, done = 0;
  scheduledStops(day).forEach(loc => {
    total++;
    if (checklist[visitKey(loc)] === "completed") done++;
  });
  return { total, done };
};

const scopeTimeInfo = (days, checklist, addedPlaces) => {
  let totalHours = 0, completedHours = 0, pendingHours = 0;
  let completedCount = 0, pendingCount = 0, totalCount = 0;
  days.flatMap(scheduledStops).forEach(loc => {
    const dur = loc.visit_duration_hours || 1.5;
    totalCount++;
    const status = checklist[visitKey(loc)];
    if (status === "completed") { completedHours += dur; completedCount++; }
    else if (status !== "removed") { pendingHours += dur; pendingCount++; }
    totalHours += dur;
  });
  const addedHours = addedPlaces.reduce((sum, place) => sum + (place.duration || 1.5), 0);
  // Preserve the existing rough capacity estimate; this is not the planner's calendar.
  const availableHours = days.length * 12;
  const remainingHours = Math.max(0, availableHours - completedHours - pendingHours - addedHours);
  return { totalHours, completedHours, pendingHours, addedHours, completedCount, pendingCount, totalCount, availableHours, remainingHours, dayCount: days.length };
};

// Offline peace of mind: Japan connectivity is spotty and hands are full.
// The companion caches the live trip + checklist in localStorage so the
// glance card still works with no signal (and after a refresh).
const CACHE_KEY = "ju_companion_cache";
const readTripCache = () => {
  try { const raw = localStorage.getItem(CACHE_KEY); return raw ? JSON.parse(raw) : null; } catch { return null; }
};
const writeTripCache = (itinerary, checklist) => {
  try { localStorage.setItem(CACHE_KEY, JSON.stringify({ itinerary, checklist, savedAt: Date.now() })); } catch {}
};

// Ticks made without signal wait here until the server has them (2026-10-07: they were dropped, and the next
// open overwrote them with the server's older state). One entry per visit: the latest tick wins.
const OUTBOX_KEY = "ju_companion_outbox";
const readOutbox = () => {
  try { return JSON.parse(localStorage.getItem(OUTBOX_KEY) || "{}"); } catch { return {}; }
};
const writeOutbox = (box) => {
  try { localStorage.setItem(OUTBOX_KEY, JSON.stringify(box)); } catch {}
};

// Haversine (km) between two {lat,lng}. Used to find which planned stop the
// traveller is actually nearest to, from a one-shot GPS fix.
const haversineKm = (a, b) => {
  if (!a || !b || a.lat == null || b.lat == null) return null;
  const R = 6371, toR = Math.PI / 180;
  const dLat = (b.lat - a.lat) * toR, dLng = (b.lng - a.lng) * toR;
  const s = Math.sin(dLat / 2) ** 2 + Math.cos(a.lat * toR) * Math.cos(b.lat * toR) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(s));
};

const nowMins = () => { const n = japanClock(); return +n.hour * 60 + +n.minute; };

// "Show this to staff" phrasebook — kanji/kana + romaji + English, driven by
// the traveller's own dietary needs (baked into the rescue pack). Japanese-
// only menus and cash stalls are a first-48-hours reality; a line you can
// hold up removes the whole anxiety.
const DIETARY_PHRASES = {
  vegetarian: "ベジタリアンです。肉と魚は食べられません。 (Bejitarian desu — I can't eat meat or fish.)",
  vegan: "ヴィーガンです。肉・魚・卵・乳製品なしでお願いします。 (Vegan — no meat, fish, egg, or dairy, please.)",
  pescatarian: "肉は食べません。魚介類は大丈夫です。 (No meat; seafood is okay.)",
  halal: "豚肉とアルコールは食べられません。 (No pork and no alcohol, please.)",
  kosher: "宗教上の理由で豚肉と貝は食べられません。 (No pork or shellfish, for religious reasons.)",
  gluten_free: "小麦アレルギーです。醤油も避けたいです。 (Wheat allergy — please avoid soy sauce too.)",
  dairy_free: "乳製品は食べられません。 (No dairy, please.)",
  nut_allergy: "ナッツアレルギーです。 (Nut allergy.)",
  shellfish_allergy: "甲殻類アレルギーです。 (Shellfish allergy.)",
  egg_allergy: "卵アレルギーです。 (Egg allergy.)",
  soy_allergy: "大豆アレルギーです。 (Soy allergy.)",
};
const GENERAL_PHRASES = [
  "英語のメニューはありますか？ (Eigo no menyū wa arimasu ka? — Do you have an English menu?)",
  "カードは使えますか？ (Kādo wa tsukaemasu ka? — Can I pay by card?)",
  "トイレはどこですか？ (Toire wa doko desu ka? — Where is the toilet?)",
  "おすすめは何ですか？ (Osusume wa nan desu ka? — What do you recommend?)",
];

const CASH_RE = /cash[- ]only|cash only|bring cash|no cards?\b/i;
// Prefer the reviewed card field; fall back to a text scan for old trips.
const needsCash = (loc) =>
  loc?.cash_only === true ||
  (loc?.cash_only == null && CASH_RE.test([loc?.description, loc?.insider_note, ...(loc?.tips || [])].filter(Boolean).join(" ")));

// Where is the traveller in their trip right now?
const computeTripNow = (days, checklist, opts = {}) => {
  const skip = new Set(opts.advancedPast || []);
  const sorted = [...(days || [])].sort((a, b) => a.day_number - b.day_number);
  if (!sorted.length) return { phase: "none" };
  const now = todayLocal();
  const first = sorted[0], last = sorted[sorted.length - 1];
  const firstDate = parseDateLocal(first.date), lastDate = parseDateLocal(last.date);

  if (firstDate && now < firstDate) {
    return { phase: "before", daysUntil: dayDiff(firstDate, now), day: first,
             next: findNextStop(first, checklist, skip) };
  }
  if (lastDate && now > lastDate) return { phase: "after", day: last };

  // During the trip: today's day, or the next upcoming if dates don't line up.
  let day = sorted.find(d => { const dt = parseDateLocal(d.date); return dt && dayDiff(dt, now) === 0; });
  if (!day) day = sorted.find(d => { const dt = parseDateLocal(d.date); return dt && dt >= now; }) || first;
  const next = findNextStop(day, checklist, skip);
  const stats = dayStats(day, checklist);
  return { phase: next ? "during" : "day-done", day, next, stats };
};

const CompanionPage = ({ tripConfig, navigateTo, startOver, sessionToken, setTripConfig }) => {
  const itinerary = tripConfig?.itinerary;
  const [checklist, setChecklist] = useState({});
  // Fall back to the offline cache when the app state doesn't have the trip
  // (refresh mid-trip, spotty signal) — the companion must not go blank.
  const [currentItinerary, setCurrentItinerary] = useState(() => itinerary || readTripCache()?.itinerary || null);
  // One-tap rescue moments — instant, offline, from the precomputed rescue pack
  const [quickBusy, setQuickBusy] = useState(null);
  const [rescueSheet, setRescueSheet] = useState(null); // {kind,title,subtitle,spots,situation}
  // Ground truth: a one-shot GPS fix + the stop it re-anchored to. Never tracked.
  const [here, setHere] = useState(null); // { lat, lng, stopName, at }
  const [locating, setLocating] = useState(false);
  // Self-heal: stops the user manually advanced past without marking done.
  const [advancedPast, setAdvancedPast] = useState([]);
  const [unfoldDismissed, setUnfoldDismissed] = useState(false);
  const [nudgeDismissed, setNudgeDismissed] = useState("");
  const [, setClockTick] = useState(0);
  useEffect(() => {
    const refreshClock = () => setClockTick(tick => tick + 1);
    const timer = window.setInterval(refreshClock, 30000);
    document.addEventListener("visibilitychange", refreshClock);
    return () => { window.clearInterval(timer); document.removeEventListener("visibilitychange", refreshClock); };
  }, []);
  const [phrasebookOpen, setPhrasebookOpen] = useState(false);
  const [placeSheet, setPlaceSheet] = useState(null); // Layer 3: { loc, day, tod }
  const [sheetAll, setSheetAll] = useState(false);     // Layer 4: "read everything"
  const [reserved, setReserved] = useState(() => { try { return JSON.parse(localStorage.getItem("ju_reserved") || "{}"); } catch { return {}; } });
  const toggleReserved = (key) => setReserved(prev => {
    const next = { ...prev, [key]: !prev[key] };
    try { localStorage.setItem("ju_reserved", JSON.stringify(next)); } catch {}
    return next;
  });
  const [activeDay, setActiveDay] = useState(1);
  // Recalculate Wizard state
  const [wizardOpen, setWizardOpen] = useState(false);
  const [recoveryOpen, setRecoveryOpen] = useState(false);
  const [wizardStep, setWizardStep] = useState(1); // 1=scope, 2=review, 3=confirm
  const [wizardScope, setWizardScope] = useState(null); // "day" | "city" | "trip"
  const [wizardChecklist, setWizardChecklist] = useState({}); // {name: "completed"|"removed"|"pending"}
  const [isRecalculating, setIsRecalculating] = useState(false);
  const [showCatalog, setShowCatalog] = useState(false);
  const [catalogPlaces, setCatalogPlaces] = useState([]);
  const [addedPlaces, setAddedPlaces] = useState([]);

  useEffect(() => {
    if (currentItinerary?.id) fetchChecklistState();
  }, [currentItinerary?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // when the signal comes back, the ticks made without it are sent
  useEffect(() => {
    const back = () => { if (currentItinerary?.id) fetchChecklistState(); };
    window.addEventListener("online", back);
    return () => window.removeEventListener("online", back);
  }, [currentItinerary?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Open the companion on TODAY (if the trip is underway) instead of Day 1.
  useEffect(() => {
    const t = computeTripNow(currentItinerary?.days, {});
    if ((t.phase === "during" || t.phase === "day-done") && t.day) {
      setActiveDay(t.day.day_number);
    }
  }, [currentItinerary?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Keep the offline cache fresh so the companion survives lost signal.
  useEffect(() => {
    if (currentItinerary?.id) writeTripCache(currentItinerary, checklist);
  }, [currentItinerary, checklist]);

  // send what waited in the outbox; what still cannot be sent stays there
  const flushOutbox = async () => {
    const box = readOutbox();
    for (const [key, item] of Object.entries(box)) {
      if (item.itinerary_id !== currentItinerary?.id) continue;
      try {
        await axios.post(`${API}/itinerary/checklist`, item);
        delete box[key];
      } catch { break; }      // still offline: keep the rest for next time
    }
    writeOutbox(box);
    return box;
  };

  const fetchChecklistState = async () => {
    try {
      const pending = await flushOutbox();
      const res = await axios.get(`${API}/itinerary/${currentItinerary.id}/checklist`);
      const m = {};
      res.data.states.forEach(s => { m[s.visit_id || s.location_name] = s.status; });
      // a tick the server has not received yet is newer than what it holds
      Object.values(pending).filter(i => i.itinerary_id === currentItinerary.id)
        .forEach(i => { m[i.visit_id || i.location_name] = i.status; });
      setChecklist(m);
    } catch (e) {
      // Offline: restore the last known checklist instead of erroring.
      const cached = readTripCache();
      if (cached?.checklist) setChecklist(cached.checklist);
    }
  };

  // Fire-and-forget telemetry so mid-trip failures aren't invisible to a
  // solo owner in another timezone. Never blocks or throws.
  const pingEvent = (kind, detail) => {
    try {
      axios.post(`${API}/companion/event`, { kind, detail: String(detail || "").slice(0, 300), itinerary_id: currentItinerary?.id || "" }).catch(() => {});
    } catch { /* ignore */ }
  };

  const findLoc = (name) => {
    for (const d of (currentItinerary?.days || []))
      for (const b of (d.time_blocks || []))
        for (const loc of (b.locations || []))
          if (visitKey(loc) === name || loc.name === name) return loc;
    return null;
  };

  const updateStatus = async (loc, status) => {
    const name = loc.name;
    setChecklist(prev => ({ ...prev, [visitKey(loc)]: status }));
    // Milestone: ticking off a place they specifically asked for deserves a moment.
    if (status === "completed" && findLoc(name)?.is_supplementary) {
      toast.success(`${name} — one of the places you came for. ✨`);
    }
    const item = { itinerary_id: currentItinerary.id, location_name: name, visit_id: loc.visit_id, status };
    try {
      await axios.post(`${API}/itinerary/checklist`, item);
    } catch (e) {
      // no signal: kept in the outbox and sent when the page next reaches the server
      const box = readOutbox();
      box[`${item.itinerary_id}:${item.visit_id || name}`] = item;
      writeOutbox(box);
    }
  };

  // Get days in scope
  const getScopeDays = () => {
    if (!wizardScope || !currentItinerary?.days) return [];
    const currentCity = currentItinerary.days.find(d => d.day_number === activeDay)?.city;
    switch (wizardScope) {
      case "day": return currentItinerary.days.filter(d => d.day_number === activeDay);
      case "city": return currentItinerary.days.filter(d => d.city === currentCity && d.day_number >= activeDay);
      case "trip": return currentItinerary.days.filter(d => d.day_number >= activeDay);
      default: return [];
    }
  };

  const openWizard = () => {
    if (!currentItinerary.profiler_brief) { setRecoveryOpen(true); return; }
    setWizardOpen(true);
    setWizardStep(1);
    setWizardScope(null);
    setWizardChecklist({});
    setAddedPlaces([]);
    setShowCatalog(false);
  };

  const selectScope = (scope) => {
    setWizardScope(scope);
    // Initialize wizard checklist from current checklist + "pending" for everything in scope
    const days = (() => {
      const currentCity = currentItinerary.days?.find(d => d.day_number === activeDay)?.city;
      switch (scope) {
        case "day": return currentItinerary.days?.filter(d => d.day_number === activeDay) || [];
        case "city": return currentItinerary.days?.filter(d => d.city === currentCity && d.day_number >= activeDay) || [];
        case "trip": return currentItinerary.days?.filter(d => d.day_number >= activeDay) || [];
        default: return [];
      }
    })();
    setWizardChecklist(buildWizardChecklist(days, checklist));
    setWizardStep(2);
  };

  const toggleWizardStatus = (name, toStatus) => {
    setWizardChecklist(prev => ({ ...prev, [name]: prev[name] === toStatus ? "pending" : toStatus }));
  };

  const fetchCatalogForWizard = async () => {
    try {
      const res = await axios.get(`${API}/catalog/places`);
      setCatalogPlaces(res.data.places);
      setShowCatalog(true);
    } catch (e) { toast.error("Failed to load catalog"); }
  };

  const addPlaceFromCatalog = (place) => {
    if (!addedPlaces.find(p => p.name === place.name)) {
      setAddedPlaces(prev => [...prev, place]);
    } else {
      setAddedPlaces(prev => prev.filter(p => p.name !== place.name));
    }
  };

  const [removedFromRecalculation, setRemovedFromRecalculation] = useState([]);
  const [hideRemoved, setHideRemoved] = useState(false);
  const [previewResult, setPreviewResult] = useState(null);
  const [crossDayConflicts, setCrossDayConflicts] = useState([]);

  // Use the same scheduled stops as progress and the rebuild checklist.
  const getScopeTimeInfo = () => scopeTimeInfo(getScopeDays(), wizardChecklist, addedPlaces);

  // Cross-day conflict detection
  const checkCrossDayConflicts = (placeName) => {
    const scopeDayNumbers = new Set(getScopeDays().map(d => d.day_number));
    const conflicts = [];
    currentItinerary.days?.forEach(day => {
      if (scopeDayNumbers.has(day.day_number)) return; // skip current scope
      day.time_blocks?.forEach(block => {
        block.locations?.forEach(loc => {
          if (!loc.is_optional && loc.name === placeName) {
            conflicts.push({ dayNumber: day.day_number, city: day.city, timeOfDay: block.time_of_day });
          }
        });
      });
    });
    return conflicts;
  };

  const markAlreadyVisited = (name) => {
    toggleWizardStatus(name, "completed");
    const conflicts = checkCrossDayConflicts(findLoc(name)?.name || name);
    if (conflicts.length > 0) {
      setCrossDayConflicts(prev => [...prev.filter(c => c.name !== name), ...conflicts.map(c => ({ name, ...c }))]);
    }
  };

  const handleRecalculate = async () => {
    setIsRecalculating(true);
    const completed = Object.entries(wizardChecklist).filter(([_, s]) => s === "completed").map(([n]) => n);
    const removed = Object.entries(wizardChecklist).filter(([_, s]) => s === "removed").map(([n]) => n);
    const scopeDays = getScopeDays();
    const remainingDayNumbers = scopeDays.map(d => d.day_number);

    const removedWithDays = [];
    scopeDays.forEach(day => {
      day.time_blocks?.forEach(block => {
        block.locations?.forEach(loc => {
          if (removed.includes(visitKey(loc))) {
            removedWithDays.push({ name: loc.name, dayNumber: day.day_number, neighborhood: loc.neighborhood || block.cluster_name });
          }
        });
      });
    });

    try {
      const res = await axios.post(`${API}/itinerary/recalculate`, {
        itinerary_id: currentItinerary.id,
        completed_locations: completed,
        skipped_locations: removed,
        added_places: addedPlaces.map(p => p.name),
        remaining_day_numbers: remainingDayNumbers,
        scope: wizardScope,
        current_time: new Date().toISOString(),
        current_position: here ? { lat: here.lat, lng: here.lng, observed_at: here.observed_at } : null,
        preview_only: true
      });
      // Show preview before applying
      setPreviewResult({ data: res.data, removedWithDays, completed, removed });
      setWizardStep(4);
    } catch (e) {
      if (e?.response?.status === 429) {
        toast(e.response.data?.detail || "You've used all the AI rebuilds included with this trip.", { icon: "🌿", duration: 6000 });
        setWizardOpen(false);
      } else {
        toast.error("Couldn't rebuild your plan just now — please try again.");
        pingEvent("replan_failed", e?.message);
      }
    } finally { setIsRecalculating(false); }
  };

  // ── One-tap stress moments ──────────────────────────────────────
  // "Running behind" / "We're tired" / "It's raining": ONE tap re-flows the
  // rest of TODAY through the v8 brain (walls/dietary/sacreds enforced),
  // then the existing preview sheet asks for a simple yes/no. No wizard.
  const quickReplan = async (situation) => {
    if (!currentItinerary.profiler_brief) { setRecoveryOpen(true); return; }
    const t = computeTripNow(currentItinerary?.days, checklist);
    const today = t.day?.day_number;
    if (!today) return;
    setQuickBusy(situation);
    setActiveDay(today);
    setWizardScope("day");
    const completed = Object.entries(checklist).filter(([_, s]) => s === "completed").map(([n]) => n);
    const skipped = Object.entries(checklist).filter(([_, s]) => s === "skipped").map(([n]) => n);
    try {
      const res = await axios.post(`${API}/itinerary/recalculate`, {
        itinerary_id: currentItinerary.id,
        completed_locations: completed,
        skipped_locations: skipped,
        added_places: [],
        remaining_day_numbers: [today],
        scope: "day",
        situation,
        current_time: new Date().toISOString(),
        current_position: here ? { lat: here.lat, lng: here.lng, observed_at: here.observed_at } : null,
        preview_only: true,
      });
      setPreviewResult({ data: res.data, removedWithDays: [], completed, removed: skipped });
      setWizardOpen(true);
      setWizardStep(4);
    } catch (e) {
      // 429 = the trip's AI-rebuild budget is spent. Not an error — say so
      // calmly and point at the instant options, which never run out.
      if (e?.response?.status === 429) {
        toast(e.response.data?.detail || "You've used all the AI rebuilds included with this trip.", { icon: "🌿", duration: 6000 });
      } else {
        toast.error("Couldn't replan just now — please try again.");
        pingEvent("replan_failed", e?.message);
      }
    } finally { setQuickBusy(null); }
  };

  // Instant rescue: read the precomputed pack for the next stop. No network,
  // no AI, works at 0 bars. Falls back to the live food-now endpoint only for
  // old trips generated before the pack existed.
  const RESCUE_META = {
    food: { chip: "Hungry", title: "Eat near you", sub: "Closest good spots to", situation: null },
    rain: { chip: "Rain", title: "Stay dry near you", sub: "Indoor spots minutes from", situation: "raining" },
    tired: { chip: "Tired", title: "Take it easy", sub: "Gentle options near", situation: "tired" },
    closed: { chip: "Closed?", title: "Do this instead", sub: "If it's closed, try these near", situation: null },
  };

  const openRescue = async (kind, requestedStop) => {
    const t = computeTripNow(currentItinerary?.days, checklist);
    const anchor = requestedStop || (here?.visitId ? findLoc(here.visitId) : null) || t.next?.loc;
    const anchorName = typeof anchor === "string" ? anchor : anchor?.name;
    const anchorId = typeof anchor === "object" ? anchor?.visit_id : null;
    const meta = RESCUE_META[kind];
    const pack = currentItinerary?.rescue_pack || {};
    const entry = (anchorId && pack.by_visit?.[anchorId]) || (anchorName && pack.by_stop?.[anchorName]);
    let spots = entry ? entry[kind === "tired" ? "food" : kind] : null;

    if (spots && spots.length) {
      setRescueSheet({ kind, title: meta.title, subtitle: `${meta.sub} ${entry.area || t.day?.city || "you"}.`,
        spots, situation: meta.situation, near: entry.area, city: entry.city || t.day?.city });
      return;
    }
    // Fallback (old trips without a pack): the live food endpoint for food only.
    if (kind === "food" && t.day?.day_number) {
      setQuickBusy(kind);
      try {
        const res = await axios.get(`${API}/itinerary/${currentItinerary.id}/food-now?day_number=${t.day.day_number}`);
        setRescueSheet({ kind, title: meta.title, subtitle: `Closest good spots to ${res.data.near}.`,
          spots: res.data.spots, situation: null, city: res.data.city });
      } catch (e) { toast.error("Couldn't find spots right now — check the day's food picks below."); pingEvent("foodnow_failed", e?.message); }
      finally { setQuickBusy(null); }
      return;
    }
    toast.message("Nothing precomputed nearby — try the day's own picks below.");
  };

  // Persist an in-trip edit back to the user's saved trip, so a replan
  // survives a refresh / a new device instead of living only in memory.
  const persistTripEdit = async (updated) => {
    if (!updated) return;
    // Keep the app-level trip in sync (Result page, PDF, share all read this).
    try { setTripConfig?.(prev => ({ ...(prev || {}), itinerary: updated })); } catch { /* ignore */ }
    if (!sessionToken) return;  // not signed in on this device — local only
    try {
      await axios.post(`${API}/itineraries/save`, { itinerary: updated, title: updated.title },
        { headers: { Authorization: `Bearer ${sessionToken}` } });
    } catch (e) {
      pingEvent("trip_edit_save_failed", e?.message);
    }
  };

  const confirmRecalculation = async () => {
    if (!previewResult) return;
    let applied;
    try {
      const response = await axios.post(`${API}/itinerary/recalculate/apply`, {
        itinerary_id: currentItinerary.id, replan_token: previewResult.data.replan_token,
      });
      applied = response.data;
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Couldn't save this adjustment. Your trip is unchanged.");
      return;
    }
    setCurrentItinerary(applied);
    persistTripEdit(applied);
    setRemovedFromRecalculation(prev => [...prev, ...previewResult.removedWithDays]);
    setChecklist(prev => {
      const updated = { ...prev };
      previewResult.completed.forEach(n => { updated[n] = "completed"; });
      previewResult.removed.forEach(n => { updated[n] = "skipped"; });
      return updated;
    });
    setCrossDayConflicts([]);
    setPreviewResult(null);
    setWizardOpen(false);
    toast.success("Your plan is updated and saved.");
  };

  if (!currentItinerary) return (
    <div className="min-h-screen flex flex-col items-center justify-center px-6 text-center" style={{ background: "var(--zen-bg)" }}>
      <p className="text-sm mb-4" style={{ color: "var(--zen-sub)" }}>Your trip isn't loaded on this device.</p>
      <button onClick={() => navigateTo("result")} className="zen-btn zen-btn-primary px-6 py-2.5 text-sm">Open my itinerary</button>
    </div>
  );

  const activeData = currentItinerary.days?.find(d => d.day_number === activeDay);
  const currentCity = activeData?.city;
  const scopeLabels = { day: `Rest of Day ${activeDay}`, city: `Rest of ${currentCity} stay`, trip: "Rest of the trip" };

  // Live "what now?" state, recomputed from the device clock + progress.
  const tripNow = computeTripNow(currentItinerary.days, checklist, { advancedPast });

  // One-shot geolocation (never tracked). Re-anchors "nearest" to reality by
  // finding which planned stop the traveller is actually closest to.
  const useMyLocation = () => {
    if (!navigator.geolocation) { toast.error("Location isn't available here."); return; }
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const gps = { lat: pos.coords.latitude, lng: pos.coords.longitude, observed_at: new Date(pos.timestamp).toISOString() };
        const stops = (currentItinerary?.rescue_pack?.stops || []).filter(s => s.lat != null && s.day_number === tripNow.day?.day_number);
        let best = null, bestD = Infinity;
        for (const s of stops) {
          const d = haversineKm(gps, s);
          if (d != null && d < bestD) { bestD = d; best = s; }
        }
        setLocating(false);
        // Sanity guard: if the nearest planned stop is absurdly far (>150km),
        // the traveller isn't on their trip yet (still home / GPS confused).
        // Claiming "you're near Dotonbori" from 10,000km away is the exact
        // confident-lie the whole design is built to avoid.
        if (!best || bestD > 150) {
          setHere({ ...gps, stopName: null, area: null, dist: null, tooFar: true });
          toast("You don't seem to be near today's stops yet — showing your planned order.", { icon: "🧭" });
          return;
        }
        setHere({ ...gps, visitId: best.visit_id, stopName: best.name, area: best.neighborhood, dist: Math.round(bestD * 10) / 10, tooFar: false });
        toast.success(`You're near ${best.neighborhood || best.name}.`);
      },
      (err) => { setLocating(false); toast.error("Couldn't get your location — check permissions."); pingEvent("geolocation_failed", err?.message || err?.code); },
      { enableHighAccuracy: true, timeout: 8000, maximumAge: 60000 }
    );
  };

  // ── Editorial tokens (the design handoff, mobile-first expression).
  // Obsidian surfaces are theme-fixed by design; the rest rides zen vars.
  const T = {
    RED: "#E82529", INK: "#1E1A15", WASHI: "#F5F5F0",
    HAIR_D: "rgba(240,235,224,.12)", BODY_D: "rgba(240,235,224,.72)", MUTED_D: "rgba(240,235,224,.5)",
    MONO: '"IBM Plex Mono", monospace', DISPLAY: '"Unbounded", sans-serif', SANS: '"DM Sans", system-ui, sans-serif',
  };
  const mono = (size = 10, color = "var(--zen-dim)", ls = ".16em") => ({ fontFamily: T.MONO, fontWeight: 600, fontSize: size, letterSpacing: ls, textTransform: "uppercase", color });
  const badgeLabel = (cat) => ({ HIDDEN: "HIDDEN GEM" }[(cat || "").toUpperCase()] || (cat || "").toUpperCase() || "SPOT");
  const fmtDocDate = (s) => {
    const m = /(\d{4})-(\d{2})-(\d{2})/.exec(s || "");
    if (!m) return s || "";
    return `${m[3]} ${["JAN","FEB","MAR","APR","MAY","JUN","JUL","AUG","SEP","OCT","NOV","DEC"][+m[2] - 1]} ${m[1]}`;
  };
  const dayProgress = d => dayStats(d, checklist);
  const RedRule = ({ children, style }) => (
    <p style={{ fontSize: 13, lineHeight: 1.42, borderLeft: `2px solid ${T.RED}`, paddingLeft: 11, margin: 0, ...style }}>{children}</p>
  );

  // ── Layer 0: the NOW card — the cover's DNA, answering "what now?"
  const NowCard = () => {
    const shell = (children) => (
      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
        style={{ background: T.INK, color: T.WASHI, borderRadius: 8, padding: "18px 18px 16px", marginBottom: 18 }}
        data-testid="now-card">
        {children}
      </motion.div>
    );
    const eyebrow = (txt, right) => (
      <div className="flex items-center justify-between gap-3">
        <span style={mono(10, T.RED, ".18em")}>{txt}</span>
        {right && <span style={mono(9.5, T.MUTED_D, ".14em")}>{right}</span>}
      </div>
    );

    if (tripNow.phase === "before") {
      const d = tripNow.day, ns = tripNow.next;
      return shell(<>
        {eyebrow(tripNow.daysUntil === 0 ? "STARTS TOMORROW" : `STARTS IN ${tripNow.daysUntil} DAY${tripNow.daysUntil === 1 ? "" : "S"}`)}
        <h2 style={{ fontFamily: T.DISPLAY, fontWeight: 900, fontSize: 21, lineHeight: 1.12, letterSpacing: "-.01em", marginTop: 12 }}>{d?.title || `Day 1 · ${d?.city}`}</h2>
        <p style={{ fontFamily: T.SANS, fontSize: 14, color: T.BODY_D, marginTop: 8 }}>
          {d?.city} · {fmtDocDate(d?.date)}{ns ? ` — first stop: ${ns.loc.name}` : ""}
        </p>
        <p style={{ ...mono(9.5, T.MUTED_D, ".14em"), marginTop: 14, textTransform: "none", letterSpacing: ".02em" }}>Everything is saved. We'll guide you live the moment you land.</p>
      </>);
    }

    if (tripNow.phase === "after") {
      return shell(<>
        {eyebrow("TRIP COMPLETE")}
        <h2 style={{ fontFamily: T.DISPLAY, fontWeight: 900, fontSize: 21, lineHeight: 1.12, marginTop: 12 }}>What a journey.</h2>
        <p style={{ fontFamily: T.SANS, fontSize: 14, color: T.BODY_D, marginTop: 8 }}>You walked every day of it. Tap any day above to relive it.</p>
      </>);
    }

    if (tripNow.phase === "day-done") {
      const sorted = [...(currentItinerary.days || [])].sort((a, b) => a.day_number - b.day_number);
      const idx = sorted.findIndex(x => x.day_number === tripNow.day?.day_number);
      const tomorrow = idx >= 0 ? sorted[idx + 1] : null;
      const day = tripNow.day;
      const areas = [...new Set((day?.time_blocks || []).map(b => b.cluster_name).filter(Boolean))];
      const visited = scheduledStops(day).filter(l => checklist[visitKey(l)] === "completed").map(l => l.name);
      return shell(<>
        {eyebrow("THAT'S A WRAP ON TODAY", visited.length ? `${visited.length} STOP${visited.length === 1 ? "" : "S"}` : null)}
        <h2 style={{ fontFamily: T.DISPLAY, fontWeight: 900, fontSize: 21, lineHeight: 1.12, marginTop: 12 }}>{visited.length ? "What a day." : "Nothing left on the list."}</h2>
        {/* Evening Stamp — today's neighborhoods, collected like a goshuincho page */}
        {areas.length > 0 && (
          <div className="flex flex-wrap gap-2" style={{ marginTop: 12 }}>
            {areas.map((a, i) => (
              <span key={i} data-testid="stamp" style={{ ...mono(9.5, T.RED, ".12em"), border: `1.5px solid ${T.RED}`, padding: "6px 11px", borderRadius: "50% 50% 50% 50% / 60% 60% 40% 40%", transform: `rotate(${(i % 2 ? 1 : -1) * 2}deg)`, display: "inline-block" }}>{a}</span>
            ))}
          </div>
        )}
        <p style={{ fontFamily: T.SANS, fontSize: 14, color: T.BODY_D, marginTop: 12 }}>
          {tomorrow ? `Tomorrow: ${tomorrow.title || tomorrow.city} — rest up, ${tomorrow.city} is waiting.` : "That's the whole plan. However you spend tonight, you've earned it."}
        </p>
        {tomorrow && (() => {
          // Tonight, before bed — the surprises tomorrow would otherwise spring.
          const first = scheduledStops(tomorrow)[0];
          const firstTiming = visitTiming(first, tomorrow);
          const startM = firstTiming?.start;
          // An alarm is only worth suggesting for a morning window; an hour
          // before it, never earlier than 05:30 (dawn quiet is a choice, not
          // an order).
          const alarmM = startM != null && startM <= 10 * 60 ? Math.max(5 * 60 + 30, startM - 60) : null;
          const alarm = alarmM != null ? `${String(Math.floor(alarmM / 60)).padStart(2, "0")}:${String(alarmM % 60).padStart(2, "0")}` : null;
          const cash = cashStops(tomorrow);
          const todo = beforeYouGo(currentItinerary, { dayNumber: tomorrow.day_number }).filter(x => x.type === "BOOK");
          const stops = scheduledStops(tomorrow).length;
          return (
            <div style={{ marginTop: 14, borderTop: `1px solid ${T.HAIR_D}`, paddingTop: 12 }} data-testid="tonight-before-bed">
              <div style={mono(10, T.RED, ".18em")}>TONIGHT, BEFORE BED</div>
              <div style={{ marginTop: 8, display: "flex", flexDirection: "column", gap: 6, fontFamily: T.SANS, fontSize: 13.5, color: T.BODY_D }}>
                {first && alarm && <div>⏰ {first.name}: {firstTiming.kind === "plan" ? "planned around" : "suggested from"} {firstTiming.startText}. An alarm around <strong style={{ color: T.WASHI }}>{alarm}</strong> is a starting point — allow for your route and breakfast.</div>}
                {cash.length > 0 && <div>💴 {cash.length} cash-only stop{cash.length === 1 ? "" : "s"} tomorrow ({cash.slice(0, 2).map(c => c.name).join(", ")}) — have ~¥{Math.min(20000, 4000 * cash.length).toLocaleString()} ready.</div>}
                {(tomorrow.time_blocks || []).flatMap(b => b.locations || []).flatMap(p => (p.preparation_tips || []).map(t => ({...t, place:p.name}))).map(t => <div key={`${t.place}-${t.id}`}><strong>{t.place}:</strong> {t.text}</div>)}
                {todo.map((x, i) => (
                  <label key={i} className="flex items-start gap-2" style={{ cursor: "pointer" }}>
                    <input type="checkbox" checked={!!reserved[x.text]} onChange={() => toggleReserved(x.text)} style={{ marginTop: 3, accentColor: T.RED }} />
                    <span style={{ textDecoration: reserved[x.text] ? "line-through" : "none", opacity: reserved[x.text] ? 0.6 : 1 }}>🎟 {x.place ? `${x.place}: ` : ""}{tipText(x)}</span>
                  </label>
                ))}
                <div>👟 {stops} stop{stops === 1 ? "" : "s"} across {[...new Set((tomorrow.time_blocks || []).map(b => b.cluster_name).filter(Boolean))].length} areas — comfy shoes.</div>
              </div>
            </div>
          );
        })()}
      </>);
    }

    // phase === "during"
    const { day, next, stats } = tripNow;
    const loc = next.loc;
    const t = next.incomingTransit;
    const win = visitTimingStatus(loc, day);
    const ctx = contextualTip(loc, { windowUrgent: !!win?.urgent });
    const lateNow = +japanClock().hour >= 21;
    const g = upcomingVisit(day, checklist, new Set(advancedPast));
    // Time-budget honesty: stops left vs hours of usable day left.
    const left = (day?.time_blocks || []).filter(b => b.time_of_day !== "travel").flatMap(b => b.locations || []).filter(l => !l.is_optional && !["completed","skipped"].includes(checklist[visitKey(l)]) && !advancedPast.includes(visitKey(l)));
    const needH = left.reduce((s, l) => s + (Number(l.visit_duration_hours) || 1), 0);
    const datedWindows = currentItinerary?.calendar_frame?.days?.find(d => d.date === day.date)?.windows;
    const minutesOf = value => { const [h,m] = value.split(":").map(Number); return h*60+m; };
    const hoursLeft = datedWindows ? datedWindows.reduce((sum,w) => sum + Math.max(0, minutesOf(w.end) - Math.max(nowMins(), minutesOf(w.start))), 0) / 60 : null;
    const tight = hoursLeft != null && left.length >= 1 && needH > hoursLeft;
    const end = win?.end;
    const nowM = nowMins();
    const stale = end != null && nowM > end + 60;
    return shell(<>
      {/* One dated reminder for the next accepted visit */}
      {g && nudgeDismissed !== g.key && (
        <div style={{ borderLeft: `3px solid ${T.RED}`, paddingLeft: 12, marginBottom: 14 }} data-testid="golden-nudge">
          <p style={{ fontFamily: T.SANS, fontSize: 13.5, lineHeight: 1.4 }}><strong style={{ fontWeight: 700 }}>{g.loc.name}</strong>{g.timing.kind === "plan" ? ` is planned in about ${g.delta} min. Check the route when you’re ready; this is a flexible estimate.` : ` reaches its suggested window in about ${g.delta} min. Check the route before heading over.`}</p>
          <div className="flex items-center gap-4" style={{ marginTop: 6 }}>
            <a href={mapsDirUrl(g.loc, day.city)} target="_blank" rel="noopener noreferrer" style={mono(9.5, T.RED, ".14em")}>→ TAKE ME THERE</a>
            <button onClick={() => setNudgeDismissed(g.key)} style={{ ...mono(9.5, T.MUTED_D, ".14em"), background: "none", border: "none", cursor: "pointer" }}>LATER</button>
          </div>
        </div>
      )}
      {eyebrow("NEXT UP", stats ? `${stats.done}/${stats.total} DONE TODAY` : null)}
      <h2 style={{ fontFamily: T.DISPLAY, fontWeight: 900, fontSize: 24, lineHeight: 1.08, letterSpacing: "-.015em", marginTop: 10, textWrap: "balance" }}>{loc.name}</h2>
      {loc.name_ja && <p style={{ fontFamily: T.SANS, fontSize: 14, color: T.BODY_D, marginTop: 4 }}>{loc.name_ja} <span style={mono(8.5, T.MUTED_D, ".12em")}>· show a taxi / staff</span></p>}
      <div style={{ ...mono(9.5, T.MUTED_D, ".14em"), marginTop: 8 }}>
        {(next.block?.time_of_day || "").replace("_", " ")}{(next.block?.cluster_name || loc.neighborhood) ? ` · ${next.block?.cluster_name || loc.neighborhood}` : ""}
        {needsCash(loc) && " · CASH ONLY"}
      </div>
      {win && <div style={{ ...mono(9.5, win.urgent ? T.RED : T.MUTED_D, ".14em"), marginTop: 3 }}>{win.label}</div>}
      {loc.description && <p style={{ fontFamily: T.SANS, fontSize: 14, lineHeight: 1.45, color: T.BODY_D, marginTop: 10 }}>{loc.description}</p>}
      {(ctx || loc.insider_note) && (
        <div style={{ marginTop: 10, display: "flex", flexDirection: "column", gap: 6 }}>
          {ctx && <RedRule style={{ color: T.WASHI }}><span style={{ ...mono(8.5, T.RED, ".14em"), marginRight: 6 }}>{LABELS[ctx.type]}</span>{tipText(ctx)}</RedRule>}
          {loc.insider_note && <RedRule style={{ color: T.BODY_D, fontStyle: "italic" }}>☆ {loc.insider_note}</RedRule>}
        </div>
      )}
      {tight && (
        <button onClick={() => openRescue("tired")} className="w-full text-left" data-testid="time-budget"
          style={{ marginTop: 12, padding: "9px 12px", borderRadius: 2, border: `1px solid ${T.HAIR_D}`, background: "rgba(240,235,224,.06)", cursor: "pointer" }}>
          <div style={mono(9, T.RED, ".14em")}>TIME CHECK</div>
          <div style={{ fontFamily: T.SANS, fontSize: 13, color: T.BODY_D, marginTop: 3 }}>{left.length} stops left, ~{Math.round(hoursLeft)}h in your available travel windows — that's tight. Want a lighter version? →</div>
        </button>
      )}
      {t && t.minutes ? (
        <div style={{ marginTop: 12 }}>
          <div style={mono(9.5, T.MUTED_D, ".14em")}>→ {t.minutes} MIN · {(t.method !== "walk" && t.line) ? t.line : (t.method || "transit")}{t.method !== "walk" && t.from_station && t.to_station ? ` · ${t.from_station.replace(/ Station$/i, " Stn")} → ${t.to_station.replace(/ Station$/i, " Stn")}` : ""}</div>
          {t.hint && <div style={{ fontFamily: '"DM Sans", system-ui, sans-serif', fontSize: 12, color: T.MUTED_D, marginTop: 4 }}>↳ {t.hint}</div>}
        </div>
      ) : null}
      {/* Ground truth: where "nearest" is measured from. */}
      <div style={{ ...mono(8.5, here?.area ? T.WASHI : T.MUTED_D, ".08em"), textTransform: "none", marginTop: 10 }}>
        {here?.area ? `📍 You're near ${here.area}${here.dist != null ? ` · ~${here.dist} km from ${loc.name}` : ""}`
          : here?.tooFar ? "You're not near today's stops yet — distances are from your next planned stop."
          : "Distances are from your next planned stop — tap 📍 Here to use where you actually are."}
      </div>
      {stale && (
        <div className="flex items-center gap-3" style={{ ...mono(8.5, T.MUTED_D, ".08em"), textTransform: "none", marginTop: 6 }}>
          <span>Still at {loc.name}?</span>
          <button onClick={() => setAdvancedPast(p => [...p, visitKey(loc)])} style={{ ...mono(9, T.RED, ".12em"), background: "none", border: "none", cursor: "pointer" }} data-testid="now-selfheal">NO, WE MOVED ON →</button>
        </div>
      )}
      <div className="flex items-center gap-2" style={{ marginTop: 16 }}>
        <a href={mapsDirUrl(loc, day.city)} target="_blank" rel="noopener noreferrer"
          className="flex-1 flex items-center justify-center gap-2"
          style={{ background: T.RED, color: "#fff", padding: "13px 14px", borderRadius: 2, fontFamily: T.SANS, fontWeight: 700, fontSize: 14 }} data-testid="now-navigate">
          <Navigation className="w-4 h-4" /> Take me there
        </a>
        <button onClick={() => updateStatus(loc, "completed")}
          className="flex items-center gap-1.5"
          style={{ border: `1px solid ${T.BODY_D}`, color: T.WASHI, background: "transparent", padding: "13px 16px", borderRadius: 2, fontFamily: T.SANS, fontWeight: 600, fontSize: 14, cursor: "pointer" }}
          data-testid="now-done">
          <Check className="w-4 h-4" /> Done
        </button>
      </div>
      {loc.name && rescueForVisit(currentItinerary?.rescue_pack, loc).closed?.length > 0 && (
        <button onClick={() => openRescue("closed", loc)} style={{ ...mono(9.5, T.MUTED_D, ".14em"), background: "none", border: "none", cursor: "pointer", marginTop: 12, padding: 0 }} data-testid="quick-closed">
          CLOSED WHEN YOU GOT THERE? → SEE ALTERNATES
        </button>
      )}
      {lateNow && (
        <p style={{ ...mono(8.5, T.MUTED_D, ".06em"), textTransform: "none", marginTop: 10 }}>Trains wind down around midnight — check your last connection before the final stop.</p>
      )}
      <Behind day={day} />
    </>);
  };

  // What is behind them today stays in sight: a place marked done or skipped is
  // dimmed, never removed. They may want to look at it again, or put it back.
  const Behind = ({ day }) => {
    const behind = scheduledStops(day).filter(l => ["completed", "skipped"].includes(checklist[visitKey(l)]));
    if (!behind.length) return null;
    return (
      <div style={{ marginTop: 18, paddingTop: 12, borderTop: `1px solid ${T.HAIR_D}` }} data-testid="now-behind">
        <div style={mono(9, T.MUTED_D, ".14em")}>ALREADY TODAY</div>
        {behind.map(l => {
          const skipped = checklist[visitKey(l)] === "skipped";
          return (
            <div key={visitKey(l)} className="flex items-center justify-between gap-3" style={{ marginTop: 8, opacity: 0.6 }} data-testid={`behind-${l.name}`}>
              <button onClick={() => setPlaceSheet({ loc: l, day, tod: null })} className="flex items-center gap-2 min-w-0 text-left"
                style={{ background: "none", border: "none", padding: 0, cursor: "pointer", color: T.WASHI }}>
                <span className="flex items-center justify-center shrink-0" style={{ width: 18, height: 18, borderRadius: 999, border: `1.5px solid ${T.BODY_D}` }}>
                  {!skipped && <Check className="w-3 h-3" />}
                </span>
                <span className="truncate" style={{ fontFamily: T.SANS, fontSize: 13.5, textDecoration: skipped ? "line-through" : "none" }}>{l.name}</span>
              </button>
              <button onClick={() => updateStatus(l, "pending")} style={{ ...mono(8.5, T.MUTED_D, ".12em"), background: "none", border: "none", cursor: "pointer", flexShrink: 0 }}
                data-testid={`behind-undo-${l.name}`}>PUT IT BACK</button>
            </div>
          );
        })}
      </div>
    );
  };

  // ── Layer 3: the place sheet — everything about one stop, one tap away.
  const PlaceSheet = () => {
    if (!placeSheet) return null;
    const { loc, day, tod } = placeSheet;
    const status = checklist[visitKey(loc)] || "pending";
    const groups = groupedTips(loc);
    const nItems = groups.reduce((n, g) => n + g.items.length, 0);
    const pack = rescueForVisit(currentItinerary?.rescue_pack, loc);
    const win = visitTimingStatus(loc, day);
    // Layer 3 shows the two tips that matter; Layer 4 ("read everything") the rest.
    const visibleGroups = sheetAll ? groups : (() => { let budget = 2; return groups.map(g => ({ ...g, items: g.items.filter(() => budget-- > 0) })).filter(g => g.items.length); })();
    const hiddenCount = sheetAll ? 0 : Math.max(0, nItems - 2);
    return (
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/50 z-40 flex items-end sm:items-center justify-center" onClick={() => { setPlaceSheet(null); setSheetAll(false); }}>
        <motion.div initial={{ y: 120 }} animate={{ y: 0 }} exit={{ y: 120 }} onClick={e => e.stopPropagation()}
          className="w-full sm:max-w-md rounded-t-2xl sm:rounded-2xl max-h-[88vh] overflow-y-auto"
          style={{ background: "var(--zen-s1)" }} data-testid="place-sheet">
          {(loc.image || loc.is_supplementary) && (
            <div style={{ position: "relative", height: 170, background: loc.image ? "var(--zen-s2)" : T.INK }}>
              {loc.image && <img src={placeImg(loc.image)} alt={loc.name} style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} onError={(e) => { e.target.style.display = "none"; }} />}
              {!loc.image && (
                <div className="absolute inset-0 flex flex-col items-center justify-center text-center" style={{ padding: 16 }}>
                  <FanMark size={24} toriiColor={T.WASHI} title="" style={{ marginBottom: 8 }} />
                  <span style={{ fontFamily: T.DISPLAY, fontWeight: 900, fontSize: 15, lineHeight: 1.2, color: T.WASHI }}>{loc.name}</span>
                </div>
              )}
              {loc.is_supplementary && <span className="absolute" style={{ left: 12, top: 12, ...mono(8.5, T.WASHI, ".14em"), background: T.RED, padding: "4px 8px", borderRadius: 2 }}>★ YOUR PICK</span>}
              {loc.image_source === "wikimedia" && loc.image_credit && <span className="absolute truncate" style={{ right: 10, bottom: 10, maxWidth: "60%", ...mono(7, T.WASHI, ".04em"), textTransform: "none", background: "rgba(30,26,21,.72)", padding: "2px 5px", borderRadius: 2 }}>{loc.image_credit}</span>}
              <button onClick={() => setPlaceSheet(null)} className="absolute" style={{ top: 10, right: 10, width: 32, height: 32, borderRadius: 999, background: "rgba(30,26,21,.7)", color: T.WASHI, border: "none", cursor: "pointer" }}><X className="w-4 h-4 mx-auto" /></button>
            </div>
          )}
          <div style={{ padding: "16px 18px 22px" }}>
            {!loc.image && !loc.is_supplementary && <div className="flex justify-end"><button onClick={() => setPlaceSheet(null)} style={{ color: "var(--zen-dim)", background: "none", border: "none" }}><X className="w-5 h-5" /></button></div>}
            {loc.is_supplementary && loc.reputation_signal && <p style={{ fontFamily: T.SANS, fontSize: 12.5, fontStyle: "italic", color: "var(--zen-sub)", marginBottom: 8 }}>{loc.reputation_signal}</p>}
            <div style={mono(9.5, "var(--zen-dim)", ".14em")}>
              {(tod || "").replace("_", " ")}{loc.neighborhood ? ` · ${loc.neighborhood}` : ""}{loc.visit_duration_hours ? ` · ~${formatDuration(loc.visit_duration_hours)}` : ""}
            </div>
            <div className="flex items-baseline gap-2 flex-wrap" style={{ marginTop: 6 }}>
              <h2 style={{ fontFamily: T.DISPLAY, fontWeight: 900, fontSize: 20, lineHeight: 1.1, letterSpacing: "-.01em", color: "var(--zen-text)", margin: 0 }}>{loc.name}</h2>
              <span style={{ ...mono(9, "var(--zen-text)", ".14em"), border: "1px solid var(--zen-text)", borderRadius: 999, padding: "2px 8px" }}>{badgeLabel(loc.category)}</span>

            </div>
            {loc.name_ja && <p style={{ fontFamily: T.SANS, fontSize: 14, color: "var(--zen-sub)", marginTop: 4 }}>{loc.name_ja} <span style={mono(8.5, "var(--zen-dim)", ".1em")}>· show a taxi / staff</span></p>}
            <div className="flex flex-wrap" style={{ gap: "4px 14px", marginTop: 8 }}>
              {!loc.companion_content && loc.best_hours && <span style={mono(9, win?.urgent ? T.RED : "var(--zen-dim)", ".12em")}>{win ? win.label : `BEST ${loc.best_hours}`}</span>}
              {needsCash(loc) && <span style={mono(9, "var(--zen-dim)", ".12em")}>CASH ONLY</span>}
            </div>
            {loc.description && <p style={{ fontFamily: T.SANS, fontSize: 14.5, lineHeight: 1.45, color: "var(--zen-text)", marginTop: 12 }}>{loc.description}</p>}
            {loc.companion_content && <div style={{ marginTop: 14, fontFamily: T.SANS, fontSize: 14, lineHeight: 1.5 }} data-testid="sourcebook-details">
              <p>{loc.visit_allocation?.display_text}</p>
              <details style={{ marginTop: 12 }}><summary style={{ cursor: "pointer", fontWeight: 600 }}>How much time should I allow?</summary><p>{loc.companion_content.visit_overview}</p></details>
              <details style={{ marginTop: 12 }}><summary style={{ cursor: "pointer", fontWeight: 600 }}>What makes this place special?</summary><p>{loc.companion_content.story}</p></details>
              <details style={{ marginTop: 12 }}><summary style={{ cursor: "pointer", fontWeight: 600 }}>When should I go?</summary>
                <p><strong>Opening hours.</strong> {loc.companion_content.operating?.hours}</p>
                {(loc.companion_content.best_time_advice || []).map(t => <p key={t.label}><strong>{t.label}.</strong> {t.why}</p>)}
                <p>{loc.companion_content.operating?.price} · {loc.companion_content.operating?.booking}</p>
                {(loc.companion_content.practical_links || []).filter(l => l.section === "hours" || l.section === "best").map(l => <p key={l.url}><a href={l.url} target="_blank" rel="noopener noreferrer" style={{ color: T.RED }}>{l.label}</a></p>)}
              </details>
              {[['Before you set off', loc.preparation_tips], ['Before you enter', loc.entry_tips], ['While you are here', loc.on_site_tips]].filter(([, tips]) => tips?.length).map(([label, tips]) => <details key={label} style={{ marginTop: 12 }}><summary style={{ cursor: "pointer", fontWeight: 600 }}>{label}</summary><ul style={{ paddingLeft: 18 }}>{tips.map(t => <li key={t.id} style={{ marginTop: 7 }}><strong>{t.title}.</strong> {t.text}</li>)}</ul></details>)}
              {!!loc.companion_content.nearby?.length && <details style={{ marginTop: 12 }}><summary style={{ cursor: "pointer", fontWeight: 600 }}>Feel like something nearby?</summary>{loc.companion_content.nearby.map(n => <p key={n.id}><a href={n.map_url} target="_blank" rel="noopener noreferrer" style={{ color: T.RED }}>{n.name}</a> — {n.description}</p>)}</details>}
              {!!loc.companion_content.operating?.recommended_order && <details style={{ marginTop: 12 }}><summary style={{ cursor: "pointer", fontWeight: 600 }}>What would you order?</summary><p>{loc.companion_content.operating.recommended_order}</p>{(loc.companion_content.practical_links || []).filter(l => l.section === "menu").map(l => <p key={l.url}><a href={l.url} target="_blank" rel="noopener noreferrer" style={{ color: T.RED }}>{l.label}</a></p>)}</details>}
              {!!loc.companion_content.combination_ideas?.length && <details style={{ marginTop: 12 }}><summary style={{ cursor: "pointer", fontWeight: 600 }}>What else might I enjoy?</summary><p>Ideas for another part of your day. Check the route and opening hours before adding a stop.</p>{loc.companion_content.combination_ideas.map(n => <p key={n.id}><a href={n.map_url} target="_blank" rel="noopener noreferrer" style={{ color: T.RED }}>{n.name}</a> — {n.description}</p>)}</details>}
            </div>}
            {(nItems > 0 || loc.insider_note) && (
              <div style={{ marginTop: 12, display: "flex", flexDirection: "column", gap: 9 }}>
                {visibleGroups.map(g => (
                  <div key={g.type}>
                    <div style={{ ...mono(8.5, "var(--zen-dim)", ".14em"), marginBottom: 4 }}>{g.label}</div>
                    {g.items.map((it, i) => (
                      <div key={i} className="flex items-start gap-2" style={{ marginTop: i ? 5 : 0 }}>
                        <RedRule style={{ color: "var(--zen-sub)", flex: 1 }}>{tipText(it)}</RedRule>
                        {it.type === "BOOK" && (
                          <button onClick={() => toggleReserved(it.text)} title="Mark as reserved" data-testid="reserved-toggle"
                            style={{ ...mono(8.5, reserved[it.text] ? T.RED : "var(--zen-dim)", ".12em"), border: `1px solid ${reserved[it.text] ? T.RED : "var(--zen-border-h)"}`, borderRadius: 999, padding: "3px 8px", background: "transparent", cursor: "pointer", whiteSpace: "nowrap" }}>
                            {reserved[it.text] ? "✓ BOOKED" : "BOOKED?"}
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                ))}
                {(sheetAll || nItems <= 2) && loc.insider_note && <RedRule style={{ color: "var(--zen-sub)", fontStyle: "italic" }}>☆ {loc.insider_note}</RedRule>}
                {!sheetAll && (hiddenCount > 0 || (nItems > 2 && loc.insider_note)) && (
                  <button onClick={() => setSheetAll(true)} data-testid="sheet-read-all"
                    style={{ ...mono(9.5, T.RED, ".14em"), background: "none", border: "none", cursor: "pointer", padding: 0, textAlign: "left" }}>
                    READ EVERYTHING ({hiddenCount + (loc.insider_note ? 1 : 0)} MORE) →
                  </button>
                )}
              </div>
            )}
            {(sheetAll || nItems <= 2) && loc.address && <p style={{ fontSize: 12, color: "var(--zen-dim)", marginTop: 12 }}>{loc.address}</p>}

            <div className="flex items-center gap-2" style={{ marginTop: 16 }}>
              <a href={mapsDirUrl(loc, day?.city)} target="_blank" rel="noopener noreferrer"
                className="flex-1 flex items-center justify-center gap-2"
                style={{ background: T.RED, color: "#fff", padding: "13px 14px", borderRadius: 2, fontFamily: T.SANS, fontWeight: 700, fontSize: 14 }} data-testid={`nav-${loc.name}`}>
                <Navigation className="w-4 h-4" /> Take me there
              </a>
              <button onClick={() => updateStatus(loc, status === "completed" ? "pending" : "completed")}
                style={{ border: `1px solid ${status === "completed" ? T.RED : "var(--zen-border-h)"}`, color: status === "completed" ? T.RED : "var(--zen-text)", background: "transparent", padding: "13px 14px", borderRadius: 2, fontFamily: T.SANS, fontWeight: 600, fontSize: 14, cursor: "pointer" }}
                data-testid={`check-${loc.name}`}>
                <Check className="w-4 h-4 inline" /> {status === "completed" ? "Done" : "Mark done"}
              </button>
            </div>
            <button onClick={() => {
                const note = window.prompt(`Something wrong at ${loc.name}? Tell us what you found (closed, moved, wrong price…) — it fixes the card for everyone.`);
                if (note && note.trim()) { pingEvent("card_issue", `${loc.name} (${loc.city || day?.city || ""}): ${note.trim()}`); toast.success("Thank you — we'll fix the card."); }
              }} style={{ ...mono(9, "var(--zen-dim)", ".12em"), background: "none", border: "none", cursor: "pointer", padding: 0, marginTop: 12 }} data-testid="report-card">
              SOMETHING WRONG HERE? TELL US →
            </button>
            <div className="flex items-center justify-between" style={{ marginTop: 14 }}>
              <button onClick={() => updateStatus(loc, status === "skipped" ? "pending" : "skipped")}
                style={{ ...mono(9.5, "var(--zen-dim)", ".14em"), background: "none", border: "none", cursor: "pointer", padding: 0 }} data-testid={`skip-${loc.name}`}>
                {status === "skipped" ? "↩ PUT IT BACK" : "✕ SKIP THIS STOP"}
              </button>
              {(pack.closed?.length > 0 || pack.rain?.length > 0) && (
                <button onClick={() => { setPlaceSheet(null); openRescue(pack.closed?.length ? "closed" : "rain", loc); }}
                  style={{ ...mono(9.5, T.RED, ".14em"), background: "none", border: "none", cursor: "pointer", padding: 0 }} data-testid="sheet-alternates">
                  CLOSED / RAIN? ALTERNATES →
                </button>
              )}
            </div>
          </div>
        </motion.div>
      </motion.div>
    );
  };

  // The bar's icons are the app's own line (lucide, 1.5 px stroke), never emoji:
  // the same hand that draws the rest of the page.
  const rescueButtons = [
    { key: "here", label: "Here", Icon: MapPin, onClick: useMyLocation, busy: locating, disabled: locating },
    { key: "food", label: "Hungry", Icon: Soup, onClick: () => openRescue("food") },
    { key: "rain", label: "Rain", Icon: CloudRain, onClick: () => openRescue("rain") },
    { key: "tired", label: "Tired", Icon: MoonStar, onClick: () => openRescue("tired") },
    { key: "staff", label: "Staff", Icon: MessageCircleMore, onClick: () => setPhrasebookOpen(true) },
  ];
  const hasToday = !!tripNow.day;

  return (
    <div className="min-h-screen" style={{ background: "var(--zen-bg)", paddingBottom: 92 }}>
      {recoveryOpen && <TripPreferenceRecovery itineraryId={currentItinerary.id} sessionToken={sessionToken}
        onClose={() => setRecoveryOpen(false)} onSaved={fields => {
          const updated = { ...currentItinerary, ...fields };
          setCurrentItinerary(updated);
          persistTripEdit(updated);
          setRecoveryOpen(false);
          toast.success("Preferences restored. You can now choose an adjustment.");
        }} />}
      <div className="max-w-lg mx-auto px-4 pt-4">
        {/* ── Editorial header ── */}
        <div style={{ borderBottom: "2px solid var(--zen-text)", paddingBottom: 10, paddingRight: 150 }}>
          <div className="flex items-center gap-2">
            <FanMark size={18} title="" />
            <Wordmark size={12} />
          </div>
          <div className="flex items-center gap-4" style={{ marginTop: 8 }}>
            <button onClick={() => navigateTo("result")} style={{ ...mono(9.5, "var(--zen-dim)", ".14em"), background: "none", border: "none", cursor: "pointer", padding: 0 }}>← FULL ITINERARY</button>
            <button onClick={startOver} style={{ ...mono(9.5, "var(--zen-dim)", ".14em"), background: "none", border: "none", cursor: "pointer", padding: 0 }} data-testid="companion-start-over">RESTART</button>
          </div>
        </div>

        {/* ── Layer 1: the trip strip — every day at a glance, with progress ── */}
        <div className="flex gap-2 overflow-x-auto -mx-4 px-4" style={{ paddingTop: 12, paddingBottom: 12, scrollbarWidth: "none" }} data-testid="day-strip">
          {[...(currentItinerary.days || [])].sort((a, b) => a.day_number - b.day_number).map((d) => {
            const isActive = activeDay === d.day_number;
            const isToday = tripNow.day?.day_number === d.day_number && (tripNow.phase === "during" || tripNow.phase === "day-done");
            const { done, total } = dayProgress(d);
            return (
              <button key={d.day_number} onClick={() => setActiveDay(d.day_number)} data-testid={`day-tab-${d.day_number}`}
                className="shrink-0 text-left"
                style={{ minWidth: 76, padding: "8px 10px 7px", borderRadius: 2, cursor: "pointer",
                  background: isActive ? T.RED : "var(--zen-s1)", color: isActive ? T.WASHI : "var(--zen-text)",
                  border: `1px solid ${isActive ? T.RED : "var(--zen-border-h)"}` }}>
                <div style={mono(10, isActive ? T.WASHI : "var(--zen-text)", ".14em")}>DAY {d.day_number}{isToday ? " ·" : ""}</div>
                <div style={{ ...mono(8, isActive ? "rgba(245,245,240,.75)" : "var(--zen-dim)", ".12em"), marginTop: 2 }} className="truncate">{(d.city_label || d.city || "").split("→").pop().trim()}</div>
                <div style={{ height: 2, background: isActive ? "rgba(245,245,240,.3)" : "var(--zen-border)", marginTop: 7, borderRadius: 1 }}>
                  <div style={{ height: 2, width: total ? `${Math.round(100 * done / total)}%` : 0, background: isActive ? T.WASHI : T.RED, borderRadius: 1, transition: "width .3s" }} />
                </div>
              </button>
            );
          })}
        </div>

        {/* Morning Unfold — the day revealed like a letter, once each morning */}
        {(() => {
          if (unfoldDismissed || tripNow.phase !== "during") return null;
          if (new Date().getHours() >= 11 || (tripNow.stats?.done || 0) > 0) return null;
          const d = tripNow.day;
          const areas = [...new Set((d?.time_blocks || []).map(b => b.cluster_name).filter(Boolean))];
          const stopCount = scheduledStops(d).length;
          return (
            <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
              style={{ background: "var(--zen-s2)", border: "1px solid var(--zen-border)", borderRadius: 4, padding: "16px 18px", marginBottom: 14 }} data-testid="morning-unfold">
              <div style={mono(10, T.RED, ".18em")}>GOOD MORNING</div>
              <h2 style={{ fontFamily: T.DISPLAY, fontWeight: 900, fontSize: 19, lineHeight: 1.12, color: "var(--zen-text)", marginTop: 10, textWrap: "balance" }}>{d?.title || `Day ${d?.day_number}`}</h2>
              {d?.description && <p style={{ fontFamily: T.SANS, fontSize: 13.5, color: "var(--zen-sub)", marginTop: 6 }}>{d.description}</p>}
              <p style={{ fontFamily: T.SANS, fontSize: 13.5, color: "var(--zen-sub)", marginTop: 10 }}>
                Today: {stopCount} stop{stopCount === 1 ? "" : "s"} across {areas.slice(0, 3).join(", ")}{areas.length > 3 ? " and more" : ""}. Comfy shoes — Japan is a walking city.
              </p>
              {cashStops(d).length > 0 && (
                <p style={{ fontFamily: T.SANS, fontSize: 13.5, color: "var(--zen-text)", marginTop: 6 }} data-testid="cash-today">
                  💴 {cashStops(d).length} cash-only stop{cashStops(d).length === 1 ? "" : "s"} today — have ~¥{Math.min(20000, 4000 * cashStops(d).length).toLocaleString()} before you head out (any konbini ATM takes foreign cards).
                </p>
              )}
              <button onClick={() => setUnfoldDismissed(true)} style={{ ...mono(9.5, T.RED, ".14em"), background: "none", border: "none", cursor: "pointer", marginTop: 12, padding: 0 }}>LET'S GO →</button>
            </motion.div>
          );
        })()}

        {/* ── Layer 0 ── */}
        {NowCard()}

        {/* ── Layer 2: the day ── */}
        {activeData && (
          <motion.div key={activeDay} initial={{ opacity: 0, x: 16 }} animate={{ opacity: 1, x: 0 }}>
            <div className="flex items-center justify-between gap-3" style={{ borderBottom: "2px solid var(--zen-text)", paddingBottom: 10 }}>
              <div className="flex items-center gap-3 flex-wrap">
                <span style={{ ...mono(10, T.WASHI, ".16em"), background: T.RED, borderRadius: 2, padding: "7px 10px", whiteSpace: "nowrap" }}>
                  DAY {activeData.day_number} · {(activeData.city_label || activeData.city || "").toUpperCase()}
                </span>
                <span style={mono(9.5, "var(--zen-dim)", ".14em")}>{fmtDocDate(activeData.date)}</span>
              </div>
              <button onClick={async () => {
                // the tab opens on the tap itself: Safari blocks one opened after waiting for the server
                const tab = window.open("about:blank", "_blank");
                try {
                  const res = await axios.get(`${API}/itinerary/${currentItinerary.id}/maps-url?day_number=${activeDay}`);
                  if (tab) tab.location.href = res.data.url; else window.location.href = res.data.url;
                } catch (e) {
                  if (tab) tab.close();
                  toast.error("We couldn't open the map for this day — try again in a moment.");
                }
              }} className="inline-flex items-center justify-center shrink-0" style={{ width: 34, height: 34, borderRadius: 2, border: "1px solid var(--zen-border-h)", background: "transparent", cursor: "pointer" }} data-testid="day-maps-btn">
                <Map className="w-4 h-4" style={{ color: "var(--zen-text)" }} />
              </button>
            </div>
            {activeData.title && <h2 style={{ fontFamily: T.DISPLAY, fontWeight: 900, fontSize: 19, lineHeight: 1.12, letterSpacing: "-.01em", color: "var(--zen-text)", marginTop: 12, textWrap: "balance" }}>{activeData.title}</h2>}
            {activeData.description && <p style={{ fontFamily: T.SANS, fontSize: 13.5, lineHeight: 1.45, color: "var(--zen-sub)", marginTop: 6 }}>{activeData.description}</p>}

            <div style={{ marginTop: 14 }} className="space-y-4">
              {activeData.time_blocks?.map((block, bi) => {
                const isTravel = block.time_of_day === "travel";
                const tprev = bi > 0 ? activeData.time_blocks[bi - 1]?.transit_to_next : null;
                return (
                  <div key={bi}>
                    {tprev?.minutes ? (
                      <div className="flex items-center gap-3" style={{ marginBottom: 10 }}>
                        <div className="flex-1" style={{ borderTop: "1px solid var(--zen-border)" }} />
                        <span style={mono(9, "var(--zen-dim)", ".14em")}><span style={{ color: T.RED }}>→</span> {tprev.minutes} MIN · {(tprev.method !== "walk" && tprev.line) ? tprev.line : (tprev.method || "transit")}{tprev.method !== "walk" && tprev.from_station && tprev.to_station ? ` · ${tprev.from_station.replace(/ Station$/i, " Stn")} → ${tprev.to_station.replace(/ Station$/i, " Stn")}` : ""}</span>
                        <div className="flex-1" style={{ borderTop: "1px solid var(--zen-border)" }} />
                      </div>
                    ) : null}
                    {isTravel ? (
                      <div style={{ background: "var(--zen-s2)", border: "1px solid var(--zen-border)", borderRadius: 4, padding: "11px 13px" }} data-testid="travel-block">
                        <div style={mono(9, "var(--zen-dim)", ".14em")}>IN TRANSIT{block.locations?.[0]?.description ? ` · ${block.locations[0].description}` : ""}</div>
                        <div style={{ fontFamily: T.SANS, fontWeight: 800, fontSize: 15, color: "var(--zen-text)", marginTop: 5 }}>{activeData.city_label || block.locations?.[0]?.name || "Travel"}</div>
                        {block.locations?.[0]?.tips?.[0] && <RedRule style={{ color: "var(--zen-sub)", marginTop: 7 }}>{block.locations[0].tips[0]}</RedRule>}
                      </div>
                    ) : (
                      <>
                        <div className="flex items-baseline justify-between gap-2" style={{ marginBottom: 7 }}>
                          <span style={mono(9.5, "var(--zen-dim)", ".14em")}>{(block.time_of_day || "").replace("_", " ")}{block.cluster_name ? ` · ${block.cluster_name}` : ""}</span>
                        </div>
                        {block.vibe_line && <p style={{ fontFamily: T.SANS, fontStyle: "italic", fontSize: 12.5, color: "var(--zen-dim)", margin: "0 0 8px" }}>{block.vibe_line}</p>}
                        <div className="space-y-2">
                          {block.locations?.map((loc, li) => {
                            const status = checklist[visitKey(loc)] || "pending";
                            const done = status === "completed", skipped = status === "skipped";
                            return (
                              <div key={li} className="flex items-stretch" data-testid={`stop-row-${loc.name}`}
                                style={{ border: "1px solid var(--zen-border)", borderRadius: 4, overflow: "hidden", background: "var(--zen-s1)", opacity: skipped ? 0.5 : done ? 0.6 : 1 }}>
                                <button onClick={() => updateStatus(loc, done ? "pending" : "completed")} title="Mark as visited" data-testid={`row-check-${loc.name}`}
                                  className="shrink-0 flex items-center justify-center" style={{ width: 44, background: "transparent", border: "none", borderRight: "1px solid var(--zen-border)", cursor: "pointer" }}>
                                  <span className="flex items-center justify-center" style={{ width: 22, height: 22, borderRadius: 999, border: `1.5px solid ${done ? T.RED : "var(--zen-border-h)"}`, background: done ? T.RED : "transparent", color: "#fff" }}>
                                    {done && <Check className="w-3.5 h-3.5" />}
                                  </span>
                                </button>
                                <div className="flex-1 min-w-0 flex items-stretch" onClick={() => setPlaceSheet({ loc, day: activeData, tod: block.time_of_day })} style={{ cursor: "pointer" }}>
                                  <div style={{ position: "relative", width: 68, flexShrink: 0, background: loc.is_supplementary && !loc.image ? T.INK : "transparent" }}>
                                    {loc.image && <img src={placeImg(loc.image)} alt="" style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover", display: "block" }} onError={(e) => { e.target.style.display = "none"; }} />}
                                    {loc.is_supplementary && <span className="absolute" style={{ left: 4, top: 4, ...mono(7, T.WASHI, ".1em"), background: T.RED, padding: "2px 4px", borderRadius: 2 }}>★ PICK</span>}
                                  </div>
                                  <div className="flex-1 min-w-0" style={{ padding: "8px 10px" }}>
                                    <div className="flex items-center gap-2" style={mono(8.5, "var(--zen-dim)", ".12em")}>
                                      <span className="truncate">{loc.neighborhood || block.cluster_name || ""}</span>
                                      {loc.visit_duration_hours && <span className="shrink-0">~{formatDuration(loc.visit_duration_hours)}</span>}
                                    </div>
                                    <div className="flex items-baseline gap-2 flex-wrap" style={{ marginTop: 3 }}>
                                      <span style={{ fontFamily: T.SANS, fontWeight: 800, fontSize: 14.5, color: "var(--zen-text)", textDecoration: skipped ? "line-through" : "none" }}>{loc.name}</span>
                                      <span style={{ ...mono(8, "var(--zen-text)", ".12em"), border: "1px solid var(--zen-border-h)", borderRadius: 999, padding: "1px 6px" }}>{badgeLabel(loc.category)}</span>
                                      {loc.meal_role && <span style={{ ...mono(8, T.RED, ".12em"), border: `1px solid ${T.RED}`, borderRadius: 999, padding: "1px 6px", textTransform: "uppercase" }}>{loc.meal_role === "dinner_premium" ? "dress-up dinner" : loc.meal_role}</span>}
                                    </div>
                                    {loc.description && <p className="truncate" style={{ fontFamily: T.SANS, fontSize: 12, color: "var(--zen-sub)", marginTop: 3 }}>{loc.description}</p>}
                                  </div>
                                  <div className="flex items-center" style={{ paddingRight: 10, color: "var(--zen-dim)" }}><ChevronRight className="w-4 h-4" /></div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                        {block.pro_tip && <RedRule style={{ color: "var(--zen-sub)", marginTop: 8, fontSize: 12.5 }}>{block.pro_tip}</RedRule>}
                      </>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Removed places from a replan */}
            {!hideRemoved && removedFromRecalculation.filter(r => r.dayNumber === activeDay).length > 0 && (
              <div style={{ marginTop: 16, borderTop: "1px dashed var(--zen-border-h)", paddingTop: 10 }} data-testid="removed-places-section">
                <div className="flex items-center justify-between">
                  <span style={mono(9, "var(--zen-dim)", ".14em")}>REMOVED FROM PLAN</span>
                  <button onClick={() => setHideRemoved(true)} style={{ ...mono(9, "var(--zen-dim)", ".12em"), background: "none", border: "none", cursor: "pointer" }} data-testid="toggle-hide-removed">HIDE</button>
                </div>
                {removedFromRecalculation.filter(r => r.dayNumber === activeDay).map((r, i) => (
                  <p key={i} style={{ fontSize: 13, color: "var(--zen-dim)", textDecoration: "line-through", marginTop: 6 }}>{r.name}</p>
                ))}
              </div>
            )}
            {hideRemoved && removedFromRecalculation.filter(r => r.dayNumber === activeDay).length > 0 && (
              <button onClick={() => setHideRemoved(false)} style={{ ...mono(9, "var(--zen-dim)", ".12em"), background: "none", border: "none", cursor: "pointer", marginTop: 12 }} data-testid="toggle-hide-removed">SHOW REMOVED</button>
            )}

            {/* Deliberate editor — for a calm moment, not a reflex. */}
            <button onClick={openWizard} className="w-full flex items-center justify-center gap-2" data-testid="recalculate-btn"
              style={{ marginTop: 20, padding: "11px", borderRadius: 2, border: "1px solid var(--zen-border-h)", background: "transparent", color: "var(--zen-sub)", fontFamily: T.SANS, fontWeight: 600, fontSize: 13, cursor: "pointer" }}>
              <RefreshCw className="w-4 h-4" /> Reshape a day
            </button>
          </motion.div>
        )}

        {/* ── Rescue bar — one thumb-reach away, always ── */}
        <div className="fixed bottom-0 left-0 right-0 z-30" style={{ background: T.INK, borderTop: `1px solid ${T.HAIR_D}`, paddingBottom: "env(safe-area-inset-bottom)" }} data-testid="rescue-bar">
          <div className="max-w-lg mx-auto grid grid-cols-5">
            {rescueButtons.map(b => {
              const disabled = b.disabled || (b.key !== "staff" && b.key !== "here" && (!hasToday || !!quickBusy));
              const lit = b.key === "here" && !!here?.area;
              const tone = lit ? T.RED : T.MUTED_D;
              return (
                <button key={b.key} onClick={b.onClick} disabled={disabled} data-testid={`quick-${b.key}`}
                  className="flex flex-col items-center justify-center gap-1.5 disabled:opacity-35"
                  style={{ padding: "12px 4px 10px", background: "transparent", border: "none", cursor: "pointer", color: tone }}>
                  <span style={{ lineHeight: 0 }}>
                    {b.busy ? <RefreshCw className="w-5 h-5 animate-spin" strokeWidth={1.5} /> : <b.Icon className="w-6 h-6" strokeWidth={1.5} />}
                  </span>
                  <span style={mono(8.5, tone, ".14em")}>{b.label}</span>
                  <span style={{ width: 22, height: 3, borderRadius: 2, background: lit ? T.RED : "transparent", marginTop: 2 }} />
                </button>
              );
            })}
          </div>
        </div>

        <AnimatePresence>{placeSheet && <PlaceSheet />}</AnimatePresence>

        {/* ===== QUICK REPLAN WIZARD ===== */}
        <AnimatePresence>
          {wizardOpen && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/40 z-30 flex items-end sm:items-center justify-center" onClick={() => !isRecalculating && setWizardOpen(false)}>
              <motion.div initial={{ y: 100 }} animate={{ y: 0 }} exit={{ y: 100 }}
                onClick={(e) => e.stopPropagation()}
                className="bg-white rounded-t-3xl sm:rounded-3xl w-full sm:max-w-lg max-h-[85vh] overflow-y-auto shadow-xl"
                data-testid="recalculate-wizard">

                {/* ===== STEP 1: Scope ===== */}
                {wizardStep === 1 && (
                  <div className="p-6">
                    <h2 className="font-heading text-xl mb-1">Time to replan?</h2>
                    <p className="text-sm text-muted-foreground mb-6">No worries — let's adjust your route in 2 minutes.</p>
                    <div className="space-y-3">
                      <button onClick={() => selectScope("day")}
                        className="w-full text-left p-4 border border-secondary/50 rounded-xl hover:border-accent-moss hover:bg-accent-moss/5 transition-all flex items-center gap-3"
                        data-testid="scope-day">
                        <div className="w-10 h-10 rounded-full bg-accent-moss/10 flex items-center justify-center shrink-0"><Clock className="w-5 h-5 text-accent-moss" /></div>
                        <div>
                          <p className="font-medium text-sm">Rest of today</p>
                          <p className="text-xs text-muted-foreground">Replan what's left of Day {activeDay}</p>
                        </div>
                      </button>
                      <button onClick={() => selectScope("city")}
                        className="w-full text-left p-4 border border-secondary/50 rounded-xl hover:border-amber-500 hover:bg-amber-50 transition-all flex items-center gap-3"
                        data-testid="scope-city">
                        <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center shrink-0"><MapPin className="w-5 h-5 text-amber-600" /></div>
                        <div>
                          <p className="font-medium text-sm">Rest of {currentCity}</p>
                          <p className="text-xs text-muted-foreground">Replan your entire remaining stay in {currentCity}</p>
                        </div>
                      </button>
                      <button onClick={() => selectScope("trip")}
                        className="w-full text-left p-4 border border-secondary/50 rounded-xl hover:border-primary hover:bg-primary/5 transition-all flex items-center gap-3"
                        data-testid="scope-trip">
                        <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center shrink-0"><Map className="w-5 h-5 text-primary" /></div>
                        <div>
                          <p className="font-medium text-sm">Rest of my trip</p>
                          <p className="text-xs text-muted-foreground">Replan everything from Day {activeDay} onwards</p>
                        </div>
                      </button>
                    </div>
                    <button onClick={() => setWizardOpen(false)} className="w-full mt-4 py-3 text-sm text-muted-foreground hover:text-foreground transition-colors">Never mind</button>
                  </div>
                )}

                {/* ===== STEP 2: Status + Edit ===== */}
                {wizardStep === 2 && !showCatalog && (() => {
                  const info = getScopeTimeInfo();
                  const scopeDays = getScopeDays();
                  const pendingPlaces = Object.entries(wizardChecklist).filter(([_, s]) => s === "pending");
                  const completedPlaces = Object.entries(wizardChecklist).filter(([_, s]) => s === "completed");
                  const removedPlaces = Object.entries(wizardChecklist).filter(([_, s]) => s === "removed");

                  return (
                    <div className="p-6">
                      {/* Conversational header */}
                      <h2 className="font-heading text-xl mb-1">{scopeLabels[wizardScope]}</h2>
                      
                      {/* Status summary card */}
                      <div className="bg-secondary/20 rounded-xl p-4 mb-4 mt-3" data-testid="replan-status">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm font-medium">{completedPlaces.length} visited · {pendingPlaces.length} pending · {removedPlaces.length} skipped</span>
                          {addedPlaces.length > 0 && <span className="text-xs text-accent-moss font-medium">+{addedPlaces.length} new</span>}
                        </div>
                        <div className="w-full bg-secondary/40 rounded-full h-2 mb-2">
                          <div className="bg-accent-moss h-2 rounded-full transition-all" style={{ width: `${Math.round((info.completedCount / Math.max(1, info.totalCount)) * 100)}%` }} />
                        </div>
                        <div className="flex justify-between text-xs text-muted-foreground">
                          <span>~{Math.round(info.pendingHours + info.addedHours)}h planned ahead</span>
                          <span className={`font-medium ${info.remainingHours < 2 ? "text-accent-moss" : "text-amber-600"}`}>
                            ~{Math.round(info.remainingHours)}h free
                          </span>
                        </div>
                      </div>

                      <p className="text-sm text-muted-foreground mb-3">
                        What stays, what goes? Tap to mark.
                      </p>

                      {/* Cross-day conflict alerts */}
                      {crossDayConflicts.length > 0 && (
                        <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 mb-3" data-testid="cross-day-alert">
                          <p className="text-xs font-medium text-amber-700 mb-1">Heads up!</p>
                          {crossDayConflicts.map((c, i) => (
                            <p key={i} className="text-xs text-amber-600">
                              {findLoc(c.name)?.name || "This place"} was also planned for Day {c.dayNumber} ({c.timeOfDay} in {c.city}). Review whether you want to visit again.
                            </p>
                          ))}
                        </div>
                      )}

                      {/* Places list with status buttons */}
                      <div className="space-y-1 max-h-[35vh] overflow-y-auto pr-1">
                        {scopeDays.map(day => (
                          <div key={day.day_number}>
                            {scopeDays.length > 1 && (
                              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mt-3 mb-1.5 sticky top-0 bg-white py-1">
                                Day {day.day_number} — {day.city}
                              </p>
                            )}
                            {day.time_blocks?.map(block => scheduledLocations(block).map((loc, li) => {
                              const ws = wizardChecklist[visitKey(loc)] || "pending";
                              return (
                                <div key={`${day.day_number}-${li}`} className={`flex items-center gap-2 p-2.5 rounded-lg transition-all ${
                                  ws === "completed" ? "bg-accent-moss/10 border border-accent-moss/20" : ws === "removed" ? "bg-red-50/50 border border-red-200/30" : "bg-white border border-secondary/30"
                                }`}>
                                  <button onClick={() => markAlreadyVisited(visitKey(loc))}
                                    title="Already visited"
                                    className={`w-7 h-7 rounded-full border-2 flex items-center justify-center transition-all shrink-0 ${
                                      ws === "completed" ? "bg-accent-moss border-accent-moss text-white scale-110" : "border-secondary/50 hover:border-accent-moss"
                                    }`}>
                                    {ws === "completed" && <Check className="w-4 h-4" />}
                                  </button>
                                  <div className="flex-1 min-w-0">
                                    <span className={`text-sm ${ws === "removed" ? "line-through text-muted-foreground" : "font-medium"}`}>{loc.name}</span>
                                    <div className="flex items-center gap-1.5">
                                      <span className="text-xs text-muted-foreground">{block.cluster_name}</span>
                                      {loc.visit_duration_hours && <span className="text-xs text-accent-moss">~{formatDuration(loc.visit_duration_hours)}</span>}
                                    </div>
                                  </div>
                                  <button onClick={() => toggleWizardStatus(visitKey(loc), "removed")}
                                    title="Skip this"
                                    className={`w-7 h-7 rounded-full flex items-center justify-center transition-all shrink-0 ${
                                      ws === "removed" ? "bg-red-100 text-destructive scale-110" : "text-muted-foreground/40 hover:text-destructive hover:bg-red-50"
                                    }`}>
                                    <X className="w-4 h-4" />
                                  </button>
                                </div>
                              );
                            }))}
                          </div>
                        ))}

                        {/* Added places */}
                        {addedPlaces.length > 0 && (
                          <div>
                            <p className="text-xs font-medium text-accent-moss uppercase tracking-wide mt-3 mb-1.5">Adding to plan</p>
                            {addedPlaces.map((p, i) => (
                              <div key={i} className="flex items-center gap-2 p-2.5 rounded-lg bg-accent-moss/5 border border-accent-moss/20">
                                <Plus className="w-4 h-4 text-accent-moss shrink-0" />
                                <div className="flex-1 min-w-0">
                                  <span className="text-sm font-medium">{p.name}</span>
                                  <span className="text-xs text-accent-moss ml-2">~{p.duration || 1.5}h</span>
                                </div>
                                <button onClick={() => setAddedPlaces(prev => prev.filter(x => x.name !== p.name))} className="text-muted-foreground hover:text-destructive p-1"><X className="w-3 h-3" /></button>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>

                      {/* Action buttons */}
                      <div className="flex gap-2 mt-4">
                        <button onClick={fetchCatalogForWizard}
                          className="flex-1 py-3 text-sm text-accent-moss border border-accent-moss/30 rounded-xl hover:bg-accent-moss/5 transition-colors flex items-center justify-center gap-1.5"
                          data-testid="wizard-browse-menu">
                          <Plus className="w-4 h-4" /> Add from menu
                        </button>
                        <button onClick={handleRecalculate} disabled={isRecalculating}
                          className="flex-1 py-3 text-sm bg-primary text-primary-foreground rounded-xl hover:bg-primary/90 transition-colors flex items-center justify-center gap-1.5 disabled:opacity-60 font-medium"
                          data-testid="wizard-confirm">
                          {isRecalculating ? <><RefreshCw className="w-4 h-4 animate-spin" /> Replanning...</> : <>Replan now <ArrowRight className="w-4 h-4" /></>}
                        </button>
                      </div>
                      <button onClick={() => { setWizardStep(1); setShowCatalog(false); setCrossDayConflicts([]); }} className="w-full mt-2 py-2 text-xs text-muted-foreground hover:text-foreground transition-colors">Change scope</button>
                    </div>
                  );
                })()}

                {/* ===== STEP 2b: City-filtered Catalog ===== */}
                {wizardStep === 2 && showCatalog && (() => {
                  const info = getScopeTimeInfo();
                  const alreadyInPlan = new Set([
                    ...Object.keys(wizardChecklist).map(key => findLoc(key)?.name || key),
                    ...addedPlaces.map(p => p.name)
                  ]);
                  const filteredCatalog = catalogPlaces.filter(p => {
                    if (alreadyInPlan.has(p.name)) return false;
                    // Only offer places in the city being replanned — otherwise
                    // a Kyoto day could get a Tokyo spot added to it.
                    if (currentCity && p.city && p.city !== currentCity) return false;
                    return true;
                  });

                  return (
                    <div className="p-6">
                      <div className="flex items-center justify-between mb-1">
                        <h2 className="font-heading text-lg">Add to your plan</h2>
                        <button onClick={() => setShowCatalog(false)} className="text-sm text-accent-moss font-medium" data-testid="catalog-done-btn">
                          Done ({addedPlaces.length})
                        </button>
                      </div>
                      
                      {/* Time counter */}
                      <div className="bg-secondary/20 rounded-lg p-3 mb-4 flex items-center justify-between" data-testid="time-counter">
                        <span className="text-xs text-muted-foreground">
                          {addedPlaces.length} added · ~{Math.round(info.addedHours)}h
                        </span>
                        <span className={`text-xs font-medium ${info.remainingHours < 2 ? "text-amber-600" : "text-accent-moss"}`}>
                          ~{Math.round(info.remainingHours)}h still available
                        </span>
                      </div>

                      <p className="text-xs text-muted-foreground mb-3">{filteredCatalog.length} places in {currentCity}</p>

                      <div className="space-y-2 max-h-[50vh] overflow-y-auto pr-1">
                        {filteredCatalog.map((p, i) => {
                          const dur = p.duration || 1.5;
                          const fits = dur <= info.remainingHours;
                          const isAdded = addedPlaces.find(a => a.name === p.name);
                          return (
                            <div key={i} onClick={() => fits && addPlaceFromCatalog(p)}
                              className={`flex items-center gap-3 p-3 rounded-xl border transition-all ${
                                isAdded ? "border-accent-moss bg-accent-moss/5" : fits ? "border-secondary/50 hover:border-accent-moss/30 cursor-pointer" : "border-secondary/30 opacity-40 cursor-not-allowed"
                              }`} data-testid={`catalog-item-${p.name?.replace(/\s+/g, '-')}`}>
                              <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center shrink-0 ${
                                isAdded ? "bg-accent-moss border-accent-moss text-white" : "border-secondary/50"}`}>
                                {isAdded && <Check className="w-3 h-3" />}
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium">{p.name}</p>
                                <p className="text-xs text-muted-foreground truncate">{p.punch_phrase || p.description || `${p.city} · ${p.display_category || p.category}`}</p>
                              </div>
                              <div className="text-right shrink-0">
                                <span className="text-xs text-accent-moss">~{dur}h</span>
                                {!fits && !isAdded && <p className="text-[10px] text-amber-600">won't fit</p>}
                              </div>
                            </div>
                          );
                        })}
                        {filteredCatalog.length === 0 && (
                          <p className="text-sm text-muted-foreground text-center py-8">No more places available</p>
                        )}
                      </div>
                    </div>
                  );
                })()}

                {/* ===== STEP 4: Preview ===== */}
                {wizardStep === 4 && previewResult && (() => {
                  const scopeDays = previewResult.data.days?.filter(d => {
                    const scopeDayNums = getScopeDays().map(sd => sd.day_number);
                    return scopeDayNums.includes(d.day_number);
                  }) || [];

                  return (
                    <div className="p-6">
                      <h2 className="font-heading text-xl mb-1">Here's your updated plan</h2>
                      <p className="text-sm text-muted-foreground mb-4">Take a look — does this work for you?</p>

                      <div className="space-y-4 max-h-[50vh] overflow-y-auto pr-1">
                        {scopeDays.map(day => (
                          <div key={day.day_number} className="border border-secondary/30 rounded-xl overflow-hidden">
                            <div className="bg-secondary/15 px-4 py-2.5 flex items-center justify-between">
                              <div>
                                <span className="text-xs text-muted-foreground">Day {day.day_number}</span>
                                <h3 className="font-medium text-sm">{day.title}</h3>
                              </div>
                              <span className="text-xs text-accent-moss font-medium">{day.city}</span>
                            </div>
                            <div className="p-3 space-y-2">
                              {day.time_blocks?.map((block, bi) => (
                                <div key={bi} className={`rounded-lg p-3 ${
                                  block.time_of_day === "morning" ? "bg-amber-50/50" : block.time_of_day === "afternoon" ? "bg-blue-50/50" : "bg-indigo-50/50"
                                }`}>
                                  <div className="flex items-center gap-2 mb-1.5">
                                    <span className="text-xs font-medium capitalize text-muted-foreground">{block.time_of_day}</span>
                                    <span className="text-xs text-muted-foreground">{block.start_time} - {block.end_time}</span>
                                    <span className="text-xs bg-white/70 px-2 py-0.5 rounded-full ml-auto">{block.cluster_name}</span>
                                  </div>
                                  {block.locations?.map((loc, li) => (
                                    <div key={li} className="flex items-center gap-2 py-1">
                                      <div className="w-1.5 h-1.5 rounded-full bg-accent-moss shrink-0" />
                                      <span className="text-sm">{loc.name}</span>
                                      {loc.visit_duration_hours && <span className="text-xs text-muted-foreground ml-auto">~{formatDuration(loc.visit_duration_hours)}</span>}
                                    </div>
                                  ))}
                                  {block.pro_tip && <p className="text-xs text-muted-foreground mt-1 italic">Tip: {block.pro_tip}</p>}
                                </div>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>

                      {/* Removed places summary */}
                      {previewResult.removedWithDays.length > 0 && (
                        <div className="mt-3 p-3 bg-red-50/50 rounded-lg">
                          <p className="text-xs text-muted-foreground mb-1">Removed from plan:</p>
                          <div className="flex flex-wrap gap-1">
                            {previewResult.removedWithDays.map((r, i) => (
                              <span key={i} className="text-xs line-through text-muted-foreground bg-white px-2 py-0.5 rounded">{r.name}</span>
                            ))}
                          </div>
                        </div>
                      )}

                      <div className="flex gap-2 mt-5">
                        <button onClick={() => { setWizardStep(2); setPreviewResult(null); }}
                          className="flex-1 py-3 text-sm border border-secondary/50 rounded-xl hover:bg-secondary/20 transition-colors flex items-center justify-center gap-1.5"
                          data-testid="preview-change">
                          <ChevronLeft className="w-4 h-4" /> Change something
                        </button>
                        <button onClick={confirmRecalculation}
                          className="flex-1 py-3 text-sm bg-accent-moss text-white rounded-xl hover:bg-accent-moss/90 transition-colors flex items-center justify-center gap-1.5 font-medium"
                          data-testid="preview-confirm">
                          <Check className="w-4 h-4" /> Looks good!
                        </button>
                      </div>
                    </div>
                  );
                })()}

              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* ===== RESCUE SHEET — instant, offline, from the precomputed pack ===== */}
        <AnimatePresence>
          {rescueSheet && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/40 z-40 flex items-end sm:items-center justify-center" onClick={() => setRescueSheet(null)}>
              <motion.div initial={{ y: 100 }} animate={{ y: 0 }} exit={{ y: 100 }} onClick={e => e.stopPropagation()}
                className="w-full sm:max-w-md rounded-t-3xl sm:rounded-3xl p-6 max-h-[80vh] overflow-y-auto"
                style={{ background: "var(--zen-s1)" }} data-testid="rescue-sheet">
                <div className="flex items-center justify-between mb-1">
                  <h2 className="font-heading text-xl" style={{ color: "var(--zen-text)" }}>{rescueSheet.title}</h2>
                  <button onClick={() => setRescueSheet(null)} style={{ color: "var(--zen-dim)" }}><X className="w-5 h-5" /></button>
                </div>
                <p className="text-sm mb-4" style={{ color: "var(--zen-sub)" }}>{rescueSheet.subtitle}</p>
                {(rescueSheet.spots || []).length === 0 && (
                  <p className="text-sm py-6 text-center" style={{ color: "var(--zen-dim)" }}>Nothing precomputed nearby — your day's own picks are your best bet.</p>
                )}
                <div className="space-y-3">
                  {(rescueSheet.spots || []).map((s, i) => (
                    <div key={i} className="rounded-xl p-3.5" style={{ background: "var(--zen-s2)", border: "1px solid var(--zen-border)" }}>
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <p className="text-sm font-medium" style={{ color: "var(--zen-text)" }}>{s.name}</p>
                          <p className="text-xs" style={{ color: "var(--zen-dim)" }}>{s.neighborhood}{s.same_area ? " · right here" : s.distance_km != null ? ` · ${s.distance_km}km` : ""}</p>
                        </div>
                        <a href={mapsDirUrl(s, rescueSheet.city)} target="_blank" rel="noopener noreferrer"
                          className="shrink-0 text-xs inline-flex items-center gap-1 hover:opacity-70" style={{ color: "var(--zen-accent)" }}>
                          <Navigation className="w-3.5 h-3.5" /> Go
                        </a>
                      </div>
                      {s.description && <p className="text-xs mt-1.5" style={{ color: "var(--zen-sub)" }}>{s.description}</p>}
                      {s.what_to_order && <p className="text-[11px] mt-1" style={{ color: "var(--zen-sub)" }}><Utensils className="w-3 h-3 inline mr-1" style={{ color: "var(--zen-accent)" }} />Order: {s.what_to_order}</p>}
                      {!s.what_to_order && s.tip && <p className="text-[11px] mt-1" style={{ color: "var(--zen-dim)" }}>→ {s.tip}</p>}
                    </div>
                  ))}
                </div>
                {/* AI escalation — instant answers first, a full rebuild only if they choose it */}
                {rescueSheet.situation && (
                  <button onClick={() => { const s = rescueSheet.situation; setRescueSheet(null); quickReplan(s); }}
                    disabled={!!quickBusy}
                    className="w-full mt-4 py-2.5 text-sm rounded-xl transition-colors disabled:opacity-50"
                    style={{ border: "1px solid var(--zen-border)", color: "var(--zen-sub)", background: "transparent" }}
                    data-testid="rescue-escalate">
                    {quickBusy ? "Rebuilding…" : "Still stuck? Rebuild the rest of my day →"}
                  </button>
                )}
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* ===== SHOW-THIS-TO-STAFF phrasebook ===== */}
        <AnimatePresence>
          {phrasebookOpen && (() => {
            const dietary = currentItinerary?.rescue_pack?.dietary || [];
            const dietPhrases = dietary.map(d => DIETARY_PHRASES[d]).filter(Boolean);
            return (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                className="fixed inset-0 bg-black/40 z-40 flex items-end sm:items-center justify-center" onClick={() => setPhrasebookOpen(false)}>
                <motion.div initial={{ y: 100 }} animate={{ y: 0 }} exit={{ y: 100 }} onClick={e => e.stopPropagation()}
                  className="w-full sm:max-w-md rounded-t-3xl sm:rounded-3xl p-6 max-h-[80vh] overflow-y-auto"
                  style={{ background: "var(--zen-s1)" }} data-testid="phrasebook-sheet">
                  <div className="flex items-center justify-between mb-1">
                    <h2 className="font-heading text-xl" style={{ color: "var(--zen-text)" }}>Show this to staff</h2>
                    <button onClick={() => setPhrasebookOpen(false)} style={{ color: "var(--zen-dim)" }}><X className="w-5 h-5" /></button>
                  </div>
                  <p className="text-sm mb-4" style={{ color: "var(--zen-sub)" }}>Hold your phone up — they'll understand.</p>
                  {dietPhrases.length > 0 && (
                    <div className="mb-4">
                      <p className="font-mono-label mb-2" style={{ fontSize: 10, letterSpacing: ".14em", color: "var(--zen-accent)" }}>YOUR DIETARY NEEDS</p>
                      <div className="space-y-2">
                        {dietPhrases.map((p, i) => (
                          <p key={i} className="text-base rounded-xl p-3" style={{ background: "var(--zen-s2)", border: "1px solid var(--zen-accent)", color: "var(--zen-text)" }}>{p}</p>
                        ))}
                      </div>
                    </div>
                  )}
                  <p className="font-mono-label mb-2" style={{ fontSize: 10, letterSpacing: ".14em", color: "var(--zen-dim)" }}>HANDY PHRASES</p>
                  <div className="space-y-2">
                    {GENERAL_PHRASES.map((p, i) => (
                      <p key={i} className="text-base rounded-xl p-3" style={{ background: "var(--zen-s2)", border: "1px solid var(--zen-border)", color: "var(--zen-text)" }}>{p}</p>
                    ))}
                  </div>
                </motion.div>
              </motion.div>
            );
          })()}
        </AnimatePresence>
      </div>
    </div>
  );
};


export default CompanionPage;
